import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from './i18n';
import { ApiError, asBlocks, sendCvEvent, sendMessage, startChat } from './api/client';
import { getOrCreateVisitorId } from './visitorId';
import { clearSession, loadSession, saveSession } from './session';
import { generateUuidV4 } from './uuid';
import { MessageBlocksRenderer } from './components/MessageBlocks';
import { CallPanel, PhoneIcon } from './components/CallPanel';
import { useVoiceCall } from './voice/useVoiceCall';
import type {
  CarouselItem,
  ChatMessage,
  CvCardBlock,
  MessageOption,
  SuzanohConfig,
} from './types';

// 入力の最大文字数。public-api 側の上限 (chat_service.MAX_MESSAGE_LENGTH) と揃える。
// textarea の maxLength で入力・貼り付けを上限で頭打ちにし、そもそも超過分を送れなくする
// （サーバー側の message_too_long は貼り付け経路等に対する防御的な安全網として残す）。
const MAX_MESSAGE_LENGTH = 10000;

type ChatStatus = 'loading' | 'ready' | 'sending';

type FatalState =
  | 'delivery_inactive'
  | 'delivery_not_found'
  | 'budget_exceeded'
  | 'message_limit_exceeded'
  | 'subscription_inactive'
  | 'internal_error'
  | null;

interface Props {
  config: SuzanohConfig;
}

/**
 * Refusals a retry cannot clear: every /voice/start rejection except the rate
 * limit, which does clear on its own. Telling the visitor to try again would
 * waste their time, so they are pointed at a person — which is also how the
 * operator finds out.
 *
 * Kept in step with VOICE_UNAVAILABLE_CODES in widget.js.j2; the two surfaces
 * have no shared code, so a test compares the lists.
 */
const VOICE_UNAVAILABLE_CODES = [
  'voice_limit_exceeded',
  'voice_not_enabled',
  'subscription_inactive',
  'budget_exceeded',
  'delivery_inactive',
  'delivery_not_found',
  'origin_not_allowed',
];

function voiceErrorKey(endedError: string): string {
  if (endedError === 'dropped') return 'voice.dropped';
  if (endedError === 'mic_denied') return 'voice.micDenied';
  if (VOICE_UNAVAILABLE_CODES.includes(endedError)) return 'voice.unavailable';
  return 'voice.failed';
}

function classifyApiError(err: unknown): FatalState | 'rate_limit_exceeded' {
  if (err instanceof ApiError) {
    const code = err.errorCode;
    if (code === 'delivery_inactive') return 'delivery_inactive';
    if (code === 'delivery_not_found') return 'delivery_not_found';
    if (code === 'budget_exceeded') return 'budget_exceeded';
    if (code === 'message_limit_exceeded') return 'message_limit_exceeded';
    if (code === 'subscription_inactive') return 'subscription_inactive';
    if (code === 'rate_limit_exceeded' || err.status === 429) return 'rate_limit_exceeded';
  }
  return 'internal_error';
}

/**
 * 送信で回復可能なエラー（会話を壊さず toast で知らせて再入力を促す種類）の toast キーを返す。
 * fatal（全画面エラー）に落とすべきでないもの: 長文超過・入力バリデーション・上流の一時失敗。
 * 該当しなければ null（＝従来どおり fatal/rate_limit 判定に回す）。
 */
function recoverableToastKey(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const code = err.errorCode;
  if (code === 'message_too_long' || code === 'validation_error') return 'state.messageTooLong';
  if (err.status === 413 || err.status === 422) return 'state.messageTooLong';
  if (code === 'external_api_error' || err.status === 502 || err.status === 503 || err.status === 504)
    return 'state.serviceUnavailable';
  return null;
}

export function App({ config }: Props) {
  const { t } = useI18n();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [status, setStatus] = useState<ChatStatus>('loading');
  const [fatal, setFatal] = useState<FatalState>(null);
  const [toast, setToast] = useState<string | null>(null);
  const sessionTokenRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const composerTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  // テキスト量に応じて textarea の高さを自動調整。上限は CSS の max-height に委ねる
  // （超過分は内部スクロール）
  useEffect(() => {
    const el = composerTextareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxHeight = parseFloat(getComputedStyle(el).maxHeight) || 140;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [input]);

  // Dashboard で設定した theme_color を CSS var に注入。widget と同じ見た目になる。
  // accent-darker はヘッダーのグラデ用に派生色を生成。
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--accent', config.chatUi.themeColor);
    root.style.setProperty('--accent-contrast', readableForeground(config.chatUi.themeColor));
    root.style.setProperty('--accent-darker', darkenHex(config.chatUi.themeColor, 0.2));
  }, [config.chatUi.themeColor]);

  const showToast = useCallback((text: string, ttlMs = 4000) => {
    setToast(text);
    window.setTimeout(() => setToast((cur) => (cur === text ? null : cur)), ttlMs);
  }, []);

  // Voice call (SD-211). The button renders only when the delivery opted in.
  const voiceEnabled = Boolean(config.voiceEnabled) && config.isActive && !config.mockApi;
  const voice = useVoiceCall({
    deliveryId: config.deliveryId,
    visitorId: getOrCreateVisitorId(),
  });
  useEffect(() => {
    if (voice.state.endedError) {
      showToast(t(voiceErrorKey(voice.state.endedError)));
      voice.dismissError();
    }
  }, [voice.state.endedError, voice, showToast, t]);

  const initialize = useCallback(async () => {
    setStatus('loading');
    setFatal(null);
    try {
      const resp = await startChat({
        deliveryId: config.deliveryId,
        visitorId: getOrCreateVisitorId(),
      });
      sessionTokenRef.current = resp.session_token;
      const initBlocks = asBlocks(resp.initial_messages, resp.initial_message);
      if (initBlocks.length > 0) {
        setMessages([{ id: generateUuidV4(), role: 'assistant', blocks: initBlocks }]);
      }
      setStatus('ready');
    } catch (err) {
      const classified = classifyApiError(err);
      if (classified === 'rate_limit_exceeded') {
        showToast(t('state.rateLimit'));
        setStatus('ready');
      } else {
        setFatal(classified);
      }
    }
  }, [config.deliveryId, showToast, t]);

  useEffect(() => {
    // is_active=false の delivery は init 前に fatal セット (API 呼び出し前に短絡)
    if (!config.isActive) {
      setFatal('delivery_inactive');
      setStatus('ready');
      return;
    }
    // リロード復元: 有効な保存セッションがあれば /chat/start を回さず会話を復元する。
    // 期限切れ・無効セッションは送信時 401 で clear + 再初期化にフォールバックする。
    const restored = loadSession(config.deliveryId);
    if (restored) {
      sessionTokenRef.current = restored.token;
      setMessages(restored.messages);
      setStatus('ready');
      return;
    }
    void initialize();
  }, [config.isActive, config.deliveryId, initialize]);

  // 会話状態をリロード復元用に保存する（token は ref のため messages 変化を契機に保存）。
  useEffect(() => {
    if (sessionTokenRef.current && messages.length > 0) {
      saveSession(config.deliveryId, sessionTokenRef.current, messages);
    }
  }, [messages, config.deliveryId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  const sendUserText = useCallback(
    async (rawText: string, opts?: { isRetry?: boolean }) => {
      const text = rawText.trim();
      if (!text || !sessionTokenRef.current || status !== 'ready') return;
      const userMsg: ChatMessage = {
        id: generateUuidV4(),
        role: 'user',
        blocks: [{ type: 'text', text }],
      };
      setMessages((cur) => [...cur, userMsg]);
      setStatus('sending');
      try {
        const resp = await sendMessage({ sessionToken: sessionTokenRef.current, message: text });
        const blocks = asBlocks(resp.messages, resp.message);
        setMessages((cur) => [
          ...cur,
          { id: generateUuidV4(), role: 'assistant', blocks },
        ]);
        setStatus('ready');
        // シナリオが END ノードに到達すると、サーバーはセッションを破棄し terminated=true を
        // 返す。保存済みセッションを破棄して token を捨てることで、次回アクセスは
        // loadSession が空になり新規会話が始まる。消費しないと 24h TTL の間、終了済み
        // 会話が復活し続ける（モバイル/デスクトップ問わず発生する）。
        if (resp.terminated) {
          clearSession(config.deliveryId);
          sessionTokenRef.current = null;
        }
      } catch (err) {
        // 復元したセッションが期限切れ/無効なら保存を破棄して新規セッションを張り直し、
        // ユーザーの発言を 1 回だけ再送する（発言を握り潰さない）。再送でも 401 なら
        // ループを避けて通常のエラー処理に落とす（isRetry ガード）。
        if (
          !opts?.isRetry &&
          err instanceof ApiError &&
          (err.status === 401 || err.errorCode === 'invalid_session')
        ) {
          clearSession(config.deliveryId);
          sessionTokenRef.current = null;
          setMessages([]);
          await initialize();
          await sendUserText(text, { isRetry: true });
          return;
        }
        // 回復可能なエラー（長文超過・バリデーション・上流一時失敗）は fatal にせず
        // toast で知らせ、楽観追加したユーザー発話を取り消して会話履歴を保つ。
        const recoverable = recoverableToastKey(err);
        if (recoverable) {
          showToast(t(recoverable));
          setMessages((cur) => cur.slice(0, -1));
          setStatus('ready');
          return;
        }
        const classified = classifyApiError(err);
        if (classified === 'rate_limit_exceeded') {
          showToast(t('state.rateLimit'));
          setStatus('ready');
        } else {
          setFatal(classified);
        }
      }
    },
    [status, showToast, t, config.deliveryId, initialize],
  );

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    await sendUserText(text);
  }, [input, sendUserText]);

  // クイックリプライ (OptionsBlock の message action) でユーザーに代わって即送信
  const handleSelectOption = useCallback(
    (opt: MessageOption) => {
      // URI アクションは MessageBlocks コンポーネント側で新規タブを開くため
      // ここでは message アクションのみ扱う前提だが、念のため再判定する。
      if (opt.action_type !== 'message') return;
      void sendUserText(opt.value);
    },
    [sendUserText],
  );

  // カルーセルカードクリックの CV 計測 (fire-and-forget、遷移は妨げない)
  const handleCarouselItemClick = useCallback((item: CarouselItem) => {
    if (!sessionTokenRef.current) return;
    sendCvEvent({
      sessionToken: sessionTokenRef.current,
      payload: {
        item_id: item.id,
        cv: item.cv ?? null,
        link_url: item.link_url || null,
        card_kind: 'carousel',
      },
    });
  }, []);

  // CV カードクリックの CV 計測。カルーセルと同じ経路で card_kind だけ変える
  const handleCvCardClick = useCallback((block: CvCardBlock) => {
    if (!sessionTokenRef.current) return;
    sendCvEvent({
      sessionToken: sessionTokenRef.current,
      payload: {
        item_id: block.goal_key,
        cv: block.cv ?? null,
        link_url: block.link_url || null,
        card_kind: 'cv_card',
      },
    });
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        void handleSend();
      }
    },
    [handleSend],
  );

  // internal_error の Retry は会話を破棄しない。送信起因ならセッションが生きているので
  // fatal を解除して会話に戻すだけ（従来は initialize() が startChat をやり直し履歴を
  // 初期メッセージだけに置換して消していた）。セッションが無い＝初期化失敗時のみ再初期化。
  const handleFatalRetry = useCallback(() => {
    if (sessionTokenRef.current) {
      setFatal(null);
      setStatus('ready');
    } else {
      void initialize();
    }
  }, [initialize]);

  if (fatal) {
    return (
      <div className="app">
        <Header config={config} />
        <FatalView
          fatal={fatal}
          onRetry={fatal === 'internal_error' ? handleFatalRetry : undefined}
        />
        {toast && <div className="toast" role="status">{toast}</div>}
      </div>
    );
  }

  const inactive = !config.isActive;
  const disabled = inactive || status !== 'ready';
  // Dashboard 側で placeholder 未設定なら locale 既定値にフォールバック
  const placeholderText = config.chatUi.placeholderText || t('input.placeholder');

  return (
    <div className="app">
      <Header
        config={config}
        onStartCall={voiceEnabled ? () => void voice.startCall() : undefined}
        callActive={voice.state.status !== 'idle'}
      />
      {voice.state.status !== 'idle' && (
        <CallPanel
          state={voice.state}
          levelSinkRef={voice.levelSinkRef}
          botName={config.botName}
          t={t}
          onEnd={voice.endCall}
          onToggleMute={voice.toggleMute}
          onChip={voice.sendChip}
        />
      )}
      <div className="messages" role="log" aria-label={t('a11y.messages')} aria-live="polite">
        {messages.map((m, idx) => (
          <MessageRow
            key={m.id}
            message={m}
            config={config}
            t={t}
            onSelectOption={handleSelectOption}
            onCarouselItemClick={handleCarouselItemClick}
            onCvCardClick={handleCvCardClick}
            isLatestAssistantMessage={
              m.role === 'assistant' && idx === messages.length - 1
            }
            inputDisabled={disabled}
          />
        ))}
        {/* 初回の startChat 待ち (loading) / 送信中 (sending) どちらも応答待ち状態として typing 表示 */}
        {(status === 'loading' || status === 'sending') && (
          <TypingIndicator config={config} t={t} />
        )}
        <div ref={messagesEndRef} />
      </div>
      {inactive && <div className="status-banner">{t('state.inactive')}</div>}
      <div className="composer">
        <div className="composer-inner">
          <textarea
            ref={composerTextareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={inactive ? '' : placeholderText}
            disabled={disabled}
            aria-label={placeholderText}
            maxLength={MAX_MESSAGE_LENGTH}
            rows={1}
          />
          <button
            type="button"
            className="send-btn"
            aria-label={t('input.send')}
            onClick={() => void handleSend()}
            disabled={disabled || !input.trim()}
          >
            <SendIcon />
          </button>
        </div>
      </div>
      {toast && <div className="toast" role="status">{toast}</div>}
    </div>
  );
}

function Header({
  config,
  onStartCall,
  callActive,
}: {
  config: SuzanohConfig;
  onStartCall?: () => void;
  callActive?: boolean;
}) {
  const { t } = useI18n();
  const online = config.isActive;
  return (
    <div className="header">
      <div className="header-avatar-wrap">
        {/* pulse ring は online 時のみ。停止中に動かすと活動中と誤認させる */}
        {online && (
          <>
            <span className="pulse-ring" aria-hidden="true" />
            <span className="pulse-ring pulse-ring-delayed" aria-hidden="true" />
          </>
        )}
        <Avatar config={config} t={t} size="md" />
        <span
          className={`header-status-dot ${online ? 'online' : 'offline'}`}
          aria-hidden="true"
        />
      </div>
      <div className="header-text">
        <div className="bot-name">{config.botName}</div>
        <div className="header-subtitle">
          {online ? t('header.subtitle.online') : t('header.subtitle.offline')}
        </div>
      </div>
      {onStartCall && !callActive && (
        <button
          type="button"
          className="header-call-btn"
          onClick={onStartCall}
          aria-label={t('voice.startCall')}
          title={t('voice.startCall')}
        >
          <PhoneIcon />
        </button>
      )}
    </div>
  );
}

function Avatar({
  config,
  t,
  size,
}: {
  config: SuzanohConfig;
  t: (key: string) => string;
  size: 'sm' | 'md';
}) {
  const initial = (config.botName || '?').trim().charAt(0).toUpperCase();
  return (
    <div className={`avatar-frame avatar-${size}`}>
      {config.avatarUrl ? (
        <img className="avatar" src={config.avatarUrl} alt={t('a11y.avatar')} />
      ) : (
        <span className="avatar-fallback" aria-hidden="true">
          {initial}
        </span>
      )}
    </div>
  );
}

function MessageRow({
  message,
  config,
  t,
  onSelectOption,
  onCarouselItemClick,
  onCvCardClick,
  isLatestAssistantMessage,
  inputDisabled,
}: {
  message: ChatMessage;
  config: SuzanohConfig;
  t: (key: string) => string;
  onSelectOption: (opt: MessageOption) => void;
  onCarouselItemClick: (item: CarouselItem) => void;
  onCvCardClick: (block: CvCardBlock) => void;
  isLatestAssistantMessage: boolean;
  inputDisabled: boolean;
}) {
  if (message.role === 'assistant') {
    return (
      <div className="row row-assistant">
        <Avatar config={config} t={t} size="sm" />
        <div className="assistant-blocks">
          <MessageBlocksRenderer
            blocks={message.blocks}
            onSelectOption={onSelectOption}
            onCarouselItemClick={onCarouselItemClick}
            onCvCardClick={onCvCardClick}
            isLatestAssistantMessage={isLatestAssistantMessage}
            disabled={inputDisabled}
            t={t}
          />
        </div>
      </div>
    );
  }
  // ユーザー発話は常に単一 TextBlock。万一複数ブロックきても text の連結で良い。
  const userText = message.blocks
    .map((b) => (b.type === 'text' ? b.text : ''))
    .filter(Boolean)
    .join('\n');
  return (
    <div className="row row-user">
      <div className="bubble user">{userText}</div>
    </div>
  );
}

function SendIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 19V5" />
      <path d="M5 12l7-7 7 7" />
    </svg>
  );
}

function TypingIndicator({
  config,
  t,
}: {
  config: SuzanohConfig;
  t: (key: string) => string;
}) {
  return (
    <div className="row row-assistant" aria-label={t('input.sending')}>
      <Avatar config={config} t={t} size="sm" />
      <div className="bubble assistant typing">
        <span className="dot" />
        <span className="dot" />
        <span className="dot" />
      </div>
    </div>
  );
}

/**
 * 背景色に対して読みやすい前景色 (白 or 黒) を返す。
 * relative luminance > 0.5 なら黒、それ以下は白。テーマカラーが薄い色の時に
 * 白文字だと潰れるのを防ぐ。
 */
function readableForeground(hex: string): string {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return '#ffffff';
  const r = parseInt(normalized.slice(0, 2), 16) / 255;
  const g = parseInt(normalized.slice(2, 4), 16) / 255;
  const b = parseInt(normalized.slice(4, 6), 16) / 255;
  // sRGB relative luminance (WCAG)
  const adj = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const luminance = 0.2126 * adj(r) + 0.7152 * adj(g) + 0.0722 * adj(b);
  return luminance > 0.5 ? '#1f1f1f' : '#ffffff';
}

/** hex (#RRGGBB) を指定割合だけ暗くする (0..1)。ヘッダーのグラデ用派生色生成。 */
function darkenHex(hex: string, amount: number): string {
  const n = hex.replace('#', '');
  if (n.length !== 6) return hex;
  const adj = (start: number) => {
    const v = parseInt(start === 0 ? n.slice(0, 2) : start === 2 ? n.slice(2, 4) : n.slice(4, 6), 16);
    return Math.max(0, Math.round(v * (1 - amount)));
  };
  return (
    '#' +
    adj(0).toString(16).padStart(2, '0') +
    adj(2).toString(16).padStart(2, '0') +
    adj(4).toString(16).padStart(2, '0')
  );
}

function FatalView({ fatal, onRetry }: { fatal: FatalState; onRetry?: () => void }) {
  const { t } = useI18n();
  const titleKey =
    fatal === 'delivery_inactive' || fatal === 'subscription_inactive'
      ? 'state.inactive'
      : fatal === 'delivery_not_found'
        ? 'state.notFound'
        : fatal === 'budget_exceeded' || fatal === 'message_limit_exceeded'
          ? 'state.budgetExceeded'
          : 'state.internalError';
  const description = fatal === 'delivery_not_found' ? t('state.notFound.description') : null;
  return (
    <div className="fullscreen-error" role="alert">
      <h1>{t(titleKey)}</h1>
      {description && <p>{description}</p>}
      {onRetry && (
        <button className="retry" onClick={onRetry}>
          {t('state.retry')}
        </button>
      )}
    </div>
  );
}
